import { useCallback, useEffect, useState } from "react";
import { Box, Grid2 as Grid, Typography } from "@mui/material";
import { useAnalyticsDataStore } from "../../../../config/advertiser.stores";
import { FormSelect } from "../../../../components/Shared/Form";
import LoadingOverlay from "../../../../components/Shared/LoadingOverlay/index";
import RefreshButton from "../../../../components/Shared/RefreshButton";
import adService from "../../../../services/ad.service";
import {
    CampaignCountWidget,
    CountryPlatformWidget,
    ImpressionsWidget,
    ImpressionsChartWidget,
    QualityImpressionWidget,
    SpendWidget,
} from "../../../../components/Advertiser/Widgets/index";

const Analytics = () => {
    const [timePeriod, setTimePeriod] = useState(28);
    const [isLoading, setIsLoading] = useState(false);

    const performance = useAnalyticsDataStore((state) => state.performance);
    const setPerformance = useAnalyticsDataStore((state) => state.setPerformance);
    const campaigns = useAnalyticsDataStore((state) => state.campaigns);
    const setCampaigns = useAnalyticsDataStore((state) => state.setCampaigns);

    const getTimePeriodString = useCallback(() => {
        const today = new Date();
        const prior = new Date();
        prior.setDate(today.getDate() - timePeriod);
        if (timePeriod === -1) {
            prior.setDate(1);
            prior.setMonth(0);
            prior.setFullYear(2024);
        }
        const formatToday = today.toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric'});
        const yearFormat = today.getFullYear() === prior.getFullYear() ? undefined : "numeric";
        const formatPrior = prior.toLocaleDateString('default', { month: 'short', day: 'numeric', year: yearFormat});
        return `${formatPrior} - ${formatToday}`;
    }, [timePeriod]);

    const fetchData = useCallback(async () => {
        // Fetch performance and campaign data
        const today = new Date();
        const startTime = timePeriod === -1 ? 0 : Math.floor(new Date().setDate(today.getDate() - timePeriod) / 1000);
        const endTime = Math.floor(today.getTime() / 1000);

        setIsLoading(true);
        const performanceResponse = await adService.queryAllPerformance(startTime, endTime);
        const campaignsResponse = await adService.query();
        setIsLoading(false);
        
        // Only use approved campaigns that have been active since the start time
        const campaigns = campaignsResponse.campaigns.filter((campaign) => 
            (campaign.approvalStatus === "approved") && campaign.endTime >= startTime
        );

        setPerformance(performanceResponse);
        setCampaigns(campaigns);
    }, [timePeriod, setCampaigns, setPerformance]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Data Filtering

    const getCountryPerformance = useCallback(() => {
        return performance.country;
    }, [performance.country]);

    const getPlatformPerformance = useCallback(() => {
        return performance.platform;
    }, [performance.platform]);

    const getImpressions = useCallback(() => {
        return campaigns.reduce((s, campaign) => s = s + (campaign.impressions ?? 0), 0);
    }, [campaigns]);

    const getQualityImpressions = useCallback(() => {
        return campaigns.reduce((s, campaign) => s = s + (campaign.qualityImpressions ?? 0), 0);
    }, [campaigns]);

    const getClicks = useCallback(() => {
        return campaigns.reduce((s, campaign) => s = s + (campaign.clicks ?? 0), 0);
    }, [campaigns]);

    const getSpend = useCallback(() => {
        return campaigns.reduce((s, campaign) => s = s + (campaign.spend ?? 0), 0);
    }, [campaigns]);

    const getInteractionsByCampaign = useCallback(() => {
        return campaigns.map(campaign => {
            return {
                title: campaign.nickname,
                impressions: campaign.impressions ?? 0,
                clicks: campaign.clicks ?? 0
            };
        });
    }, [campaigns]);

    const getSpendByCampaign = useCallback(() => {
        return campaigns.map(campaign => {
            return { label: campaign.nickname ?? "", value: campaign.spend ?? 0 };
        });
    }, [campaigns]);

    const getMostQualityImpressionsCampaign = useCallback(() => {
        return campaigns.reduce(
            (a, b) => a && (a.qualityImpressions ?? 0) > (b.qualityImpressions ?? 0) ? a : b
        );
    }, [campaigns]);

    const getActiveCampaignCount = useCallback(() => {
        return campaigns.filter((campaign) => campaign.adStatus === "active").length;
    }, [campaigns]);

    const getTotalCampaignCount = useCallback(() => {
        return campaigns.length;
    }, [campaigns]);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'left',
            gap: 3,
            margin: 3
        }}>
            <LoadingOverlay open={isLoading} />
            <Grid container width={"100%"} pb={2} alignItems="center" position="relative">
                <Typography variant="h5">Performance Analytics</Typography>
                <Box position='absolute' right={0} display="flex" gap={1} alignItems="center">
                    <FormSelect
                        id="timePeriodFilter"
                        label={getTimePeriodString()}
                        value={timePeriod}
                        setValue={setTimePeriod}
                        items={["Last 7 days", "Last 28 days", "Last 90 days", "Last 365 days", "Lifetime"]}
                        itemValues={[7, 28, 90, 365, -1]}
                        resettable={false}
                        sxProps={{ minWidth: '200px' }}
                        size={2}
                    />
                    <Box><RefreshButton onRefresh={fetchData} disabled={isLoading} /></Box>
                </Box>
            </Grid>
            {campaigns.length === 0 ? (
                <Typography minWidth="800px">No data to display for the selected time period.</Typography>
            ) : (
                <Grid container spacing={2} width="100%" minWidth="800px">
                    <ImpressionsWidget impressions={getImpressions()} clicks={getClicks()} />
                    <QualityImpressionWidget
                        qualityImpressions={getQualityImpressions()}
                        campaign={getMostQualityImpressionsCampaign()}
                    />
                    <ImpressionsChartWidget interactionsByCampaign={getInteractionsByCampaign()}/>
                    <CountryPlatformWidget 
                        countryData={getCountryPerformance()}
                        platformData={getPlatformPerformance()}
                    />
                    <SpendWidget
                        spend={getSpend()}
                        timePeriod={timePeriod}
                        spendByCampaign={getSpendByCampaign()}
                    />
                    <CampaignCountWidget
                        active={getActiveCampaignCount()}
                        total={getTotalCampaignCount()}
                    />
                </Grid>
            )}
        </Box>
    )
}

export default Analytics;
